import { useEffect, useState } from "react";
import { useParams, Link, useNavigate, Navigate } from "react-router-dom";
import Seo from "@/components/Seo";
import useCurrentState from "@/routes/useCurrentState";
import { statePath } from "@/lib/stateRegistry";
import { ArrowLeft, ArrowRight, Lock, Leaf, Check, AlertTriangle, Sun } from "lucide-react";
import ProviderLogo from "../components/ProviderLogo";
import { fetchPlan, fetchProvider } from "../lib/api";
import CostBasisLine from "../components/CostBasisLine";
import DocumentLinks from "../components/DocumentLinks";
import PlanDisclosureBlock from "../components/PlanDisclosureBlock";
import CompensationDisclosure from "../components/CompensationDisclosure";
import usePlanCosts, { COST_TIERS, formatDollars, formatLongDate, tierFor } from "../lib/planCosts";
import { formatSolarBuyback } from "../lib/solarBuyback";
import { EARLY_TERMINATION_FAQ_PATH, ETF_EXCEPTIONS_INLINE_LINK_TEXT } from "../lib/etfExceptionsTexts";

export default function PlanDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const state = useCurrentState();
  const [plan, setPlan] = useState(null);
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  // Estimated monthly cost is the server's number (GET /api/plans/{id}/costs). Nothing is computed here.
  const costs = usePlanCosts(id);

  useEffect(() => {
    setLoading(true);
    fetchPlan(id)
      .then(async (p) => {
        setPlan(p);
        const prov = await fetchProvider(p.provider_id);
        setProvider(prov);
      })
      .catch(() => navigate(statePath(state.slug, "plans")))
      .finally(() => setLoading(false));
  }, [id, navigate, state]);

  if (loading) return <div className="max-w-[1400px] mx-auto px-6 py-20 text-slate-500">Loading…</div>;
  if (!plan) return null;
  // A plan that is not offered in this state must not render under this state path.
  if (!plan.states || !plan.states.includes(state.code)) return <Navigate to={statePath(state.slug, "plans")} replace />;

  const verifiedOn = formatLongDate(plan.verified_at);
  const effectiveOn = formatLongDate(costs.data && costs.data.rate_effective_date);
  const solarBuyback = formatSolarBuyback(plan);

  return (
    <div data-testid="plan-detail-page" data-state={state.slug}>
      <Seo title={`${plan.name} in ${state.name}`} description={`${plan.name} from ${provider ? provider.name : "a provider"}: term, prices and fees.`} path={statePath(state.slug, `plans/${plan.id}`)} noindex />
      <section className="border-b border-slate-100">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-6">
          <Link to={statePath(state.slug, "plans")} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-[#0A2540]" data-testid="back-link">
            <ArrowLeft className="w-4 h-4" /> Back To Plans
          </Link>
        </div>
      </section>

      <section className="border-b border-slate-100">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-8 min-w-0">
            <div className="flex items-center gap-4 mb-6">
              <div className="rounded-xl overflow-hidden">
                <ProviderLogo provider={provider} size={64} />
              </div>
              <div>
                <p className="text-xs text-slate-500">{provider?.name}</p>
                <h1 className="font-display text-4xl md:text-5xl tracking-tight font-extrabold leading-tight text-[#0A2540]">{plan.name}</h1>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 mb-8">
              <span className="px-3 py-1 rounded-full bg-slate-50 border border-slate-100 text-xs font-semibold text-slate-600">{plan.energy_type}</span>
              <span className="px-3 py-1 rounded-full bg-slate-50 border border-slate-100 text-xs font-semibold text-slate-600">{plan.plan_type} rate</span>
              <span className="px-3 py-1 rounded-full bg-slate-50 border border-slate-100 text-xs font-semibold text-slate-600">{plan.term_months} mo term</span>
              {plan.green_energy_pct >= 100 && (
                <span className="px-3 py-1 rounded-full bg-emerald-50 text-xs font-semibold text-emerald-700 flex items-center gap-1">
                  <Leaf className="w-3 h-3" /> 100% green
                </span>
              )}
              {solarBuyback && (
                <span
                  className="px-3 py-1 rounded-full bg-[#FFF7E6] text-xs font-semibold text-[#B45309] flex items-center gap-1"
                  data-testid="plan-solar-buyback"
                >
                  <Sun className="w-3 h-3" /> {solarBuyback}
                </span>
              )}
            </div>

            <p className="text-lg text-zinc-700 mb-8">{plan.description}</p>

            {/* Pricing table */}
            <div className="rounded-2xl border border-slate-100 overflow-hidden mb-10">
              <div className="bg-[#0A2540] text-white px-6 py-4">
                <p className="text-overline text-[#4ADE80]">Electricity Facts Label</p>
                <p className="font-display text-xl font-bold">Pricing Breakdown</p>
              </div>
              <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="plan-cost-table" aria-busy={costs.status === "loading"}>
                <caption className="sr-only">Average Price And Estimated Monthly Cost At Three Usage Levels</caption>
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th scope="col" className="text-left p-4 text-overline text-slate-500">Usage</th>
                    <th scope="col" className="text-right p-4 text-overline text-slate-500">Average Price Per kWh</th>
                    <th scope="col" className="text-right p-4 text-overline text-slate-500">Estimated Monthly Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {COST_TIERS.map((kwh) => {
                    const tier = tierFor(costs, kwh);
                    const price = tier && Number.isFinite(tier.average_price_cents_per_kwh) ? tier.average_price_cents_per_kwh : plan[`rate_${kwh}`];
                    return (
                      <tr key={kwh} className="border-b border-slate-100 last:border-b-0" data-testid={`cost-row-${kwh}`}>
                        <td className="p-4 text-base">{kwh} kWh</td>
                        <td className="p-4 text-right text-base font-bold text-[#0A2540]">{Number.isFinite(price) ? `${price.toFixed(1)}¢` : "Unavailable"}</td>
                        <td className="p-4 text-right text-base font-bold text-[#0A2540]" data-testid={`cost-${kwh}`}>
                          {costs.status === "loading" ? "…" : tier ? formatDollars(tier.estimated_monthly_cost) : "Unavailable"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
              <div className="border-t border-slate-100 bg-slate-50 p-4 text-sm space-y-1">
                {costs.status === "unavailable" && (
                  <p role="status" data-testid="cost-unavailable">The estimated monthly cost is not available for this plan right now.</p>
                )}
                <CostBasisLine plan={plan} variant="primary" testId="cost-basis" className="text-zinc-700" />
                {verifiedOn && <p data-testid="plan-verified-on">Rates Verified As Of {verifiedOn}</p>}
                {effectiveOn && <p className="text-zinc-600" data-testid="plan-effective-on">Rate Effective {effectiveOn}</p>}
              </div>
            </div>

            {/* Texas disclosures (Design Specification Section 9.4) */}
            <div className="mb-10 space-y-5">
              <PlanDisclosureBlock effectiveDate={effectiveOn} testId="plan-disclosure" />
              <div className="border border-slate-200 rounded-xl p-5" data-testid="plan-documents-card">
                <p className="text-overline text-zinc-500 mb-3">Plan documents</p>
                <DocumentLinks plan={plan} planName={plan.name} variant="list" testId="plan-detail-docs" />
              </div>
            </div>

            {/* Features */}
            {plan.features?.length > 0 && (
              <div className="mb-10">
                <p className="text-overline text-zinc-500 mb-4">What&apos;s included</p>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 p-4 rounded-xl border border-slate-100 bg-slate-50">
                      <Check className="w-5 h-5 mt-0.5 shrink-0 text-[#15803D]" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Fine print */}
            <div className="rounded-2xl border-l-4 border-[#16A34A] bg-slate-50 p-6">
              <p className="text-overline text-zinc-500 mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Fine Print</p>
              <ul className="space-y-2 text-sm">
                <li>Monthly Fee: <span className="font-bold">${plan.monthly_fee.toFixed(2)}</span></li>
                <li data-testid="plan-detail-etf-line">
                  Early Termination Fee: <span className="font-bold">${plan.early_termination_fee.toFixed(2)}</span>
                  {plan.early_termination_fee > 0 && (
                    <>
                      {" "}—{" "}
                      <Link to={EARLY_TERMINATION_FAQ_PATH} className="underline font-normal" data-testid="plan-detail-etf-exceptions-link">
                        {ETF_EXCEPTIONS_INLINE_LINK_TEXT}
                      </Link>
                    </>
                  )}
                </li>
                <li>Term Length: <span className="font-bold">{plan.term_months} months</span></li>
                <li>Available In: <span className="font-bold">{plan.states.join(", ")}</span></li>
              </ul>
            </div>

            <CompensationDisclosure className="mt-10" testId="plan-compensation-disclosure" />
          </div>

          {/* Sidebar. Sticky only at lg+: a fixed/sticky element on a phone can cover a focused control
              (WCAG 2.4.11, Design Specification 9.1) and there is no bottom bar on customer pages. */}
          <aside className="col-span-12 lg:col-span-4 min-w-0">
            <div className="rounded-3xl border border-slate-100 shadow-sm p-8 lg:sticky lg:top-20 bg-white" data-testid="plan-cta-panel">
              <p className="text-overline text-zinc-500 mb-1">@ 1000 kWh</p>
              <p className="font-display text-7xl text-[#0A2540] leading-none">
                {plan.rate_1000.toFixed(1)}<span className="text-3xl font-normal text-zinc-500">¢</span>
              </p>
              <p className="text-sm text-zinc-500 mb-6">per kWh</p>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <Stat label="Term" value={`${plan.term_months}mo`} icon={Lock} />
                <Stat label="Green" value={`${plan.green_energy_pct}%`} icon={Leaf} highlight={plan.green_energy_pct >= 100} />
              </div>

              <Link to={statePath(state.slug, `enroll/${plan.id}`)} className="btn-green w-full" data-testid="plan-signup-btn">
                Enroll In This Plan <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, icon: Icon, highlight }) {
  return (
    <div className={`rounded-xl border p-3 ${highlight ? "bg-emerald-50 border-emerald-100" : "bg-slate-50 border-slate-100"}`}>
      <p className="text-xs text-slate-500 mb-1">{label}</p>
      <p className={`font-bold flex items-center gap-1 ${highlight ? "text-emerald-700" : "text-[#0A2540]"}`}><Icon className="w-3 h-3" /> {value}</p>
    </div>
  );
}
